import { CAAS_INDEX_SUFFIX, DISABLE_ADS } from "@src/app/constants";
import {
  G4A_REF_CODE_SIGNUP,
  G4A_REF_CODE_SUBSCRIBE,
  GA4_REF_CODE_CONTENTBLOCK_L,
  GA5_REF_CODE_CONTENTHUB_ANCHOR,
  GA5_REF_CODE_CONTENTHUB_BLOCK,
} from "@src/app/utils/ga/ref-code";
import {
  BRIGHTCOVE_CONFIG,
  DEFAULT_CONFIG_GAM,
  LOTAME_CONFIG,
  PREMUTIVE_CONFIG,
} from "@src/pubs/common/config";
import ContentHubMasonary from "@src/pubs/female/contentHubLayout/ContentBlockMasonary";
import HomeMansonaryLayout from "@src/pubs/female/Home/MansonaryLayout";

import {
  ConfigAboutUs,
  ConfigAffiliateLibrary,
  ConfigAffiliatesDeclaration,
  ConfigAffiliateShopping,
  ConfigAffiliatesProduct,
  ConfigAuthorProfile,
  ConfigBrightcove,
  ConfigCaas,
  ConfigCategory,
  ConfigContentHub,
  ConfigError,
  ConfigExploreMore,
  ConfigGAM,
  ConfigGTM,
  ConfigHome,
  ConfigLatestStory,
  ConfigLotame,
  ConfigNewsletter,
  ConfigPermutive,
  ConfigRss,
  ConfigSearch,
  ConfigSingleStory,
  ConfigSite,
  ConfigSkimlinks,
  ConfigTaboola,
  ConfigTag,
  ConfigTeaser,
  ConfigTrackingCode,
  ConfigVideoLanding,
  ConfigVideoOverlayPortrait,
} from "../types/config";
import FullWidthCarousel from "./Home/FullWidthCarousel";
import {
  facebookIconLight,
  mailIcon,
  pintrestIcon,
  telegramIcon,
  whatsappIconLight,
  xIcon,
} from "./icons/socialIcon";
import logoPath from "./logo.svg?url";

export const configSite: ConfigSite = {
  name: "FZINE Singapore",
  address: import.meta.env.VITE_PUBLIC_BASE_URL || "https://www.newmag.com.sg", // without trailing slash
  tagline: "Documenting fashion, beauty, parties & youth culture",
  description:
    "F ZINE is the authority on youth culture & fashion in Singapore. We document what matters to the next generation: fashion, beauty, pop culture & lifestyle experiences.",
  titleSeparator: "—",
  logoPath,
  pageShare: {
    media: [
      {
        name: "Facebook",
        icon: facebookIconLight,
      },
      {
        name: "Whatsapp",
        icon: whatsappIconLight,
      },
      {
        name: "Telegram",
        icon: telegramIcon,
      },
      {
        name: "X",
        icon: xIcon,
      },
      {
        name: "Pinterest",
        icon: pintrestIcon,
      },
      {
        name: "Email",
        icon: mailIcon,
      },
    ],
  },
  meta: [
    { name: "msapplication-TileColor", content: "#000" },
    { name: "theme-color", content: "#ffffff" },
    { name: "p:domain_verify", content: "7550a64d81e1c55bca2046c15d0218cc" },
  ],
  links: [
    {
      rel: "icon",
      type: "image/png",
      sizes: "48x48",
      href: "/favicon-48x48.png",
    },
    { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
    { rel: "shortcut icon", href: "/favicon.ico" },
  ],
};

export { configHeader } from "./config/configHeader";

export const configCaas: ConfigCaas = {
  index: `fzine${CAAS_INDEX_SUFFIX}`,
  tagIdPrefix: "tag:fzine-keywords@sph.com.sg,2023:",
  cuePublicationName: "fzine",
};

export const configGTM: ConfigGTM = {
  container: "GTM-5DCZ7VL",
  siteVerification: "_cGEdj2ZMeQhb_OTIAKvlCGbSxaBwct6M6XrDxywcXM",
};

export const configGAM: ConfigGAM = {
  ...DEFAULT_CONFIG_GAM,
  account: "/5908/fzine",
  debounce: true,
};

export const configLotame: ConfigLotame = {
  ...LOTAME_CONFIG,
  active: !DISABLE_ADS,
};

export const configPermutive: ConfigPermutive = {
  ...PREMUTIVE_CONFIG,
  active: !DISABLE_ADS,
};

export const configSkimlinks: ConfigSkimlinks = {
  active: !DISABLE_ADS,
  skimlinkId: "157725X1768906",
  geniuslinkId: "394418",
};

export const configBrightcove: ConfigBrightcove = {
  ...BRIGHTCOVE_CONFIG,
};

export const configSingleStory: ConfigSingleStory = {
  authorProfile: true,
  byline: {
    separator: ",\u00a0",
    moveShareToDescription: true,
    defaultCredit: "F ZINE Staff",
  },
  configShareArticle: {
    label: "Share This Story:",
    divider: {
      top: false,
    },
  },
  topDivider: false,
  gallery: {
    paginationSeparator: "/",
  },
  featureStory: {
    imageInsideMasthead: true,
    bodyMaxWidth: 658,
  },
  displayTags: {
    displayBottomTags: true,
    displayTagHeading: true,
    label: "Tags:",
  },
  relatedArticle: {
    headerText: "RELATED ARTICLES",
    hasRelatedPrefix: false,
    asLatestTeaser: true,
  },
  readProgress: true,
  readMore: "READ MORE",
  buttonVariant: "tertiary",
};

export const configLatestStory: ConfigLatestStory = {
  latestStoryLimit: 21,
  subAnchorLimit: 3,
  landscapeIndexes: [0, 1, 2],
  mainContentBlockDisplay: "grid",
  mainContentBlockMode: "landscape",
  errorLayoutMode: "landscape",
  portraitImageWidth: 314,
  landscapeImageWidth: 345,
};

export const configCategory: ConfigCategory = {
  pinnedTeasersLimit: 4,
  latestStoriesLimit: 4,
  subAnchorAdPositioning: 0,
  adPositioning: -1,
  exploreMorePosition: 3,
  dividerVariant: "secondary",
  exploreMoreInPinned: true,
  mainCategoryToAll: true,
  card: {
    anchorblock: {
      defaultVariant: "anchorblock-stacked",
      mobileUp: {
        defaultVariant: "anchorblock-stacked-mobile-up",
      },
    },
    subAnchor: {
      defaultVariant: "card-stacked-landscape",
      variantsByIndex: [
        [1, "card-stacked-portrait"],
        [2, "card-stacked-portrait"],
      ],
      mobileUp: {
        defaultVariant: "card-stacked-portrait-mobile-up",
        variantsByIndex: [
          [0, "card-stacked-typography-landscape-mobile-up"],
          [3, "card-stacked-landscape-mobile-up"],
        ],
      },
    },
    latestArticle: {
      defaultVariant: "card-list-original",
      mobileUp: {
        defaultVariant: "card-stacked-portrait-mobile-up",
      },
    },
  },
  latestStory: {
    landscapeIndexes: [0, 3],
    mainContentBlockMode: "portrait",
  },
};

export const configContentHub: ConfigContentHub = {
  configAds: {
    showInlineAds: false,
  },
  configMasthead: {
    heading: {
      display: true,
    },
    gradientOverlay: {
      display: true,
    },
  },
  configBodyContent: {
    display: true,
  },
  configShareButton: {
    display: false,
  },
  configEditorsNote: {
    display: false,
  },
  configCallQoute: {
    display: false,
  },
  configFeaturedSection: {
    display: true,
  },
  configDeals: {
    display: false,
    props: {
      heading: "editor's picks",
      noOfItemsInFrame: 4,
    },
  },
  configTopStories: {
    display: true,
    firstItemAnchorBlock: false,
    listingPerPage: 9,
  },
  storiesPerPage: {
    topStories: 11,
    totalStories: 20,
  },

  configListingStories: {
    mode: "portrait",
    mobileMode: "portrait",
  },
  contenthubBlockF: {
    mobileMode: "portrait",
  },
  configDisplayStandFirst: {
    display: true,
  },
  videoGalleryLandscape: {
    display: true,
    heading: {
      title: false,
      withDivider: false,
    },
    spotLight: {
      mobileLoopingArrowsBelow: true,
    },
    strip: {
      icon: true,
      size: 30,
    },
    hasPagination: false,
  },
  configBroughtYouBy: {
    headline: "Brought to you by",
  },
  cardLayout: {
    contentBlockF: {
      defaultVariant: "card-stacked-portrait-9-16",
      mobileUp: {
        defaultVariant: "card-stacked-portrait-9-16-mobile-up",
      },
    },
    listing: {
      defaultVariant: "card-stacked-portrait-9-16",
      mobileUp: {
        defaultVariant: "card-stacked-portrait-9-16-mobile-up",
      },
    },
  },
  layout: {
    stories: {
      elements: [
        {
          name: "contentBlockB",
          limit: 10,
        },
      ],
    },
    variant: [
      {
        name: "variant-mason",
        elements: [
          {
            Component: ContentHubMasonary,
            name: "contentBlockB",
            limit: 10,
          },
        ],
        ListingLayoutComponent: ContentHubMasonary,
      },
      {
        name: "variant-grid",
        elements: [
          {
            name: "contentBlockF",
            limit: 6,
          },
          {
            name: "videoplaylist",
          },
        ],
      },
    ],
  },
};

export const configTag: ConfigTag = {
  topStoriesLimit: 9,
  listedStoriesLimit: 5,
  itemSeparator: " ",
  subHeading: "",
};

export const configAmazonAffiliates = {};

export const configTaboola: ConfigTaboola = {
  enabled: true,
  publisherId: "sphmedia-fzine",
};

export const configSearch: ConfigSearch = {
  loadMoreText: "Loading more articles",
};

export const configExploreMore: ConfigExploreMore = {
  limit: 5,
  heading: "EXPLORE MORE",
  hideDivider: true,
};

export const configVideoLanding: ConfigVideoLanding = {};

export const configVideoOverlayPortrait: ConfigVideoOverlayPortrait = {};

export const configHome: ConfigHome = {
  teaser: {
    withDescription: true,
  },
  anchorsWithDescription: [
    "pinned",
    "trending",
    "topic",
    "section-culture",
    "section-fashion",
    "section-beauty",
    "section-watches-and-jewellery",
  ],
  isHeadingClickable: true,
  headingDivider: false,
  viewMore: { icon: false },
  latestArticle: {
    skipViewMore: true,
    mobileMode: "portrait",
    refCode: "latest",
  },
  contentBlockB: {
    cropsByIndex: Array.from({ length: 12 }, (_, index) => [
      index,
      "landscape",
    ]),
    arrowSize: 22,
    scrollable: true,
    mobileMode: "landscape",
    viewMore: {
      hide: true,
      text: "VIEW MORE",
    },
  },
  contentBlockD: {
    crop: "original",
    mobileMode: "original",
    skipSection: true,
  },
  contentBlockF: {
    originalIndexes: [0, 1, 2],
    hideAds: true,
    skipTeaserSection: true,
    viewMore: {
      text: "VIEW MORE",
    },
  },
  contentBlockG: {
    hideIMUMobile: true,
    imuType: "AdsSideBarDesktop",
  },
  magazineListing: {
    buttonVariant: "primary",
    midDivider: false,
  },
  newsletter: {
    newsletterBody:
      "F ZINE is the authority on youth culture and fashion — a media brand like no other in Singapore, casting a diverse and forward-looking lens on what matters to youths today.",
  },
  layoutGrid: {
    elements: [
      {
        name: "anchorBlock",
        dataSource: {
          type: "list",
          key: "anchorblock",
          fallback: "latest",
          limit: 1,
        },
      },
      {
        name: "latestArticle",
        dataSource: {
          type: "list",
          key: "pinned",
          fallback: "latest",
          limit: 3,
        },
      },
      {
        name: "contentBlockB",
        dataSource: {
          type: "list",
          key: "trending",
          fallback: "trending",
          refCode: "trending",
          min: 3,
          limit: 12,
        },
      },
      {
        name: "ads",
        dataSource: {
          type: "list",
        },
        adConfig: {
          type: "AdsSideBarMobile",
          showOnMobile: true,
        },
      },
      {
        Component: HomeMansonaryLayout,
        dataSource: {
          type: "section",
          key: "fashion",
          refCode: "block-e",
          limit: 6,
        },
      },
      {
        name: "adUnit",
      },
      {
        Component: FullWidthCarousel,
        dataSource: {
          type: "tag",
          key: "topic-0",
          refCode: "contenthub",
          min: 1,
          limit: 10,
        },
      },
      {
        name: "contentBlockG",
        dataSource: {
          type: "section",
          key: "culture",
          refCode: "block-f",
          limit: 3,
          card: {
            defaultVariant: "card-stacked-square",
            variantsByIndex: [[0, "card-stacked-portrait"]],
            mobileUp: {
              defaultVariant: "card-stacked-square-mobile-up",
              variantsByIndex: [
                [0, "card-stacked-typography-portrait-mobile-up"],
              ],
            },
          },
        },
      },
      {
        name: "contentBlockF",
        dataSource: {
          type: "tag",
          key: "topic-1",
          refCode: "contenthub",
          min: 3,
          limit: 3,
        },
      },
      {
        name: "ads",
        dataSource: {
          type: "list",
        },
        adConfig: {
          type: "AdsSideBarMobile",
          showOnMobile: true,
        },
      },
      {
        name: "contentBlockG",
        dataSource: {
          type: "section",
          key: "watches-and-jewellery",
          refCode: "contentblock",
          limit: 3,
          card: {
            defaultVariant: "card-list-square",
            variantsByIndex: [[0, "card-stacked-portrait"]],
            mobileUp: {
              defaultVariant: "card-list-square-mobile-up",
              variantsByIndex: [
                [0, "card-stacked-typography-portrait-mobile-up"],
              ],
            },
          },
        },
        variant: "secondary",
      },
      {
        name: "contentBlockD",
        dataSource: {
          type: "section",
          key: "beauty",
          refCode: "block-a",
          limit: 3,
        },
        trendingBlock: false,
      },
      {
        name: "affiliateProduct",
        dataSource: { type: "list", key: "deals" },
      },
      { name: "magazineListing" },
    ],
  },
  trendingStories: { publisher: "female" },
};

export const configNewsletter: ConfigNewsletter = {
  image: "",
  title:
    "F ZINE is the authority on youth culture and fashion — a media brand like no other in Singapore, casting a diverse and forward-looking lens on what matters to youths today.",
  body: "",
  subscriptionMessage: {
    title: "Thank you for subscribing!",
    body: `Welcome to our community! You're all set to receive the latest updates, exclusive content, and insights delivered straight to your inbox. \n\nKeep an eye out for your first newsletter hitting your inbox soon!`,
  },
};

export const configTeaser: ConfigTeaser = {
  publishDate: false,
  anchorBlock: {
    mobileCrop: "landscape",
    publishDate: false,
  },
  withDescription: true,
};

export const configTrackingCode: ConfigTrackingCode = {
  home: {
    topic: ["contenthub-a", "contenthub-b"],
    trending: "trending",
    blockg: "block-d",
    blockf: ["hometours", "block-c"],
    blockd: "howto",
  },
  category: {
    mainContentBlock: GA4_REF_CODE_CONTENTBLOCK_L,
    latestArticle: GA4_REF_CODE_CONTENTBLOCK_L,
  },
  exploremore: "exploremore",
  contenthub: {
    topstories: {
      contentBlockB: GA5_REF_CODE_CONTENTHUB_BLOCK,
      contentBlockF: GA5_REF_CODE_CONTENTHUB_BLOCK,
    },
    videoplaylist: GA5_REF_CODE_CONTENTHUB_ANCHOR,
    listing: GA5_REF_CODE_CONTENTHUB_BLOCK,
  },
  story: {
    related: "related",
  },
  subscribe: G4A_REF_CODE_SUBSCRIBE,
  signup: G4A_REF_CODE_SIGNUP,
};

export const configAffiliatesDeclaration: ConfigAffiliatesDeclaration = {
  text: "This article contains affiliate links. If you buy through these links, we may earn a small commission.",
};

export const configError: ConfigError = {
  relatedTitle: "TRENDING",
  messages: {
    status404: {
      title: "*Oof*",
      altTitle:
        "The page you were looking for doesn’t exist. Explore our trending articles or try searching again.",
      lead: " ",
    },
  },
  teaser: {
    api: "trending",
    limit: 12,
  },
  scrollable: {
    arrowSize: 20,
  },
};

export const configAuthorProfile: ConfigAuthorProfile = {
  teaserLimit: 10,
  showTeaserDescription: true,
  readMoreText: "Read",
  hideDivider: true,
};

export const configAboutUs: ConfigAboutUs = {
  standfirst: true,
  profileBlock: {
    heading: "Meet The Team",
  },
};

export const configAffiliatesProduct: ConfigAffiliatesProduct = {
  cta: {
    variant: "primary",
  },
  arrowSize: 20,
  arrowVariant: "fill",
};

export const configAffiliateShopping: ConfigAffiliateShopping = {};
export const configRss: ConfigRss = [];
export const configAffiliateLibrary: ConfigAffiliateLibrary = {
  enable: true,
};
